<template>
    <div class="tw-bg-white tw-p-4 tw-border tw-border-solid tw-border-gray-200 tw-h-full">
        <div class="tw-flex tw-items-center tw-mb-2 tw-gap-3 tw-px-x">
            <div class="tw-flex tw-items-center tw-gap-2">
                <p class="tw-p-2x tw-font-bold tw-text-lg">{{ $t('common.top-products') }}</p>
                <p class="tw-px-1 tw-bg-emerald-600 tw-text-white tw-text-sm tw-rounded">
                    {{ new Intl.NumberFormat().format(products?.length || 0) }}
                </p>
            </div>
        </div>

        <template v-if="loading">
            <div class="tw-space-y-2">
                <div v-for="i in 3" :key="i" class="tw-h-[53px] tw-bg-gray-50 tw-animate-pulse tw-p-2">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <div class="tw-aspect-square tw-w-fit tw-border-e tw-border-solid tw-border-gray-200">
                            <div class="tw-bg-gray-100 tw-w-8 tw-h-8 tw-rounded"></div>
                        </div>
                        <div class="tw-flex-1 tw-h-full tw-flex tw-flex-col tw-justify-between">
                            <div class="tw-w-32 tw-h-4 tw-bg-gray-100 tw-rounded"></div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template v-else>
            <div v-if="!paginatedProducts.length" class="tw-min-h-[237px] tw-flex tw-items-center tw-justify-center tw-p-2 tw-bg-gray-50">
                <p class="tw-text-gray-600 tw-text-center tw-text-sm tw-rounded">
                    {{ $t('messages.table-no-results') }}
                </p>
            </div>

            <div v-if="paginatedProducts.length" class="tw-p-1x tw-flex tw-flex-col tw-gap-1">
                <div v-for="p in paginatedProducts" :key="p.id" 
                    class="tw-flex md:tw-items-center md:tw-flex-row tw-flex-col tw-justify-between tw-bg-white tw-rounded tw-overflow-hidden tw-border tw-border-solid tw-border-gray-200">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <div class="tw-bg-gray-50 tw-p-1 tw-aspect-square tw-w-fit tw-border-e tw-border-y md:tw-border-y-0 tw-border-solid tw-border-gray-200">
                            <icon icon="hugeicons:package" class="tw-text-3xl tw-text-gray-300" />
                        </div>
                        <div>
                            <p class="tw-font-bold md:tw-text-sm tw-text-xs tw-text-gray-600 tw-truncate tw-max-w-[250px]">
                                {{ p.name }}
                            </p>
                            <p class="tw-text-xs tw-text-emerald-600 tw-font-semibold">
                                {{ p.reconfirmation_rate }}% {{ $t('common.reconfirmation_rate') }}
                            </p>
                        </div>
                    </div>

                    <div class="tw-px-2 tw-flex tw-items-center md:tw-gap-2 tw-flex-wrap md:tw-mt-0 tw-mt-2 md:tw-pb-0 tw-pb-2 md:tw-mx-0 tw-mx-auto tw-gap-4">
                        <div class="tw-flex tw-flex-col tw-items-center tw-justify-center">
                            <p class="md:tw-text-[10px] tw-text-sm">{{ $t('common.total_followup_orders') }}</p>
                            <p class="tw-font-bold tw-text-xs tw-text-black">{{ p.total_followup_orders }}</p>
                        </div>
                        <div class="tw-flex tw-flex-col tw-items-center tw-justify-center">
                            <p class="md:tw-text-[10px] tw-text-sm">{{ $t('common.reconfirmed') }}</p>
                            <p class="tw-font-bold tw-text-xs tw-text-emerald-600">{{ p.reconfirmed_orders }}</p>
                        </div>
                        <div class="tw-flex tw-flex-col tw-items-center tw-justify-center">
                            <p class="md:tw-text-[10px] tw-text-sm">{{ $t('common.delivered') }}</p>
                            <p class="tw-font-bold tw-text-xs tw-text-blue-600">{{ p.delivered_after_reconfirmation }}</p>
                        </div>
                        <div class="tw-flex tw-flex-col tw-items-center tw-justify-center">
                            <p class="md:tw-text-[10px] tw-text-sm">{{ $t('common.delivery_rate') }}</p>
                            <p class="tw-font-bold tw-text-xs tw-text-rose-600">{{ p.delivered_rate_after_reconfirmation }}%</p>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- Pagination Controls -->
        <div class="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
            <button 
                @click="prevPage" 
                :disabled="currentPage === 1"
                class="tw-p-1 tw-px-2 hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
                <icon 
                    :icon="$i18n.locale == 'ar' ? 'iconamoon:arrow-right-2-bold' : 'iconamoon:arrow-left-2-bold'" 
                    class="tw-text-gray-600" 
                />
            </button>
            <button 
                @click="nextPage" 
                :disabled="currentPage === totalPages || [0, 1].includes(totalPages)"
                class="tw-p-1 tw-px-2 hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
                <icon 
                    :icon="$i18n.locale == 'ar' ? 'iconamoon:arrow-left-2-bold' : 'iconamoon:arrow-right-2-bold'" 
                    class="tw-text-gray-600" 
                />
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { followup } from '@/api/dashboard';

// Reactive state
const loading = ref(true);
const products = ref([]);
const currentPage = ref(1);
const itemsPerPage = 4;

// Computed properties with safety checks
const totalPages = computed(() => {
    const productsArray = products.value || [];
    return Math.ceil(productsArray.length / itemsPerPage);
});

const paginatedProducts = computed(() => {
    const productsArray = products.value || [];
    const start = (currentPage.value - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return productsArray.slice(start, end);
});

// Navigation methods
const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

// Data fetching with proper error handling
const getData = async () => {
    loading.value = true;
    try {
        const res = await followup.topProducts();
        if (res.data.code === 'SUCCESS') {
            // Ensure we always have an array, even if API returns null/undefined
            // API response structure: res.data.data.data contains the products array
            products.value = Array.isArray(res.data.data?.data) ? res.data.data.data : [];
            currentPage.value = 1; // Reset to first page on new data
        } else {
            // Handle non-success response
            products.value = [];
            console.warn('API returned non-success code:', res.data.code);
        }
    } catch (err) {
        console.error('Failed to fetch top products:', err);
        // Ensure products is still an array on error
        products.value = [];
    } finally {
        loading.value = false;
    }
};

getData();
</script>

<style scoped>
/* Add any custom styles here if needed */
</style> 